// <iw-window>: rám okna (titulek, tlačítka), přesun, změna velikosti, přichycení, stavy. Obsah dodává pohled (view).
import { h, clamp } from '../dom';
import { t } from '../i18n';
import { icon } from '../icons';
import { contextMenu, type MenuItem } from '../components/menu';

export type WindowState = 'normal' | 'maximized' | 'minimized';

export interface WindowGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WindowView {
  readonly el: HTMLElement;
  refresh?(): void;
  getState?(): unknown;
  setState?(state: unknown): void;
  canClose?(): Promise<boolean>;
  destroy?(): void;
  focus?(): void;
  /** Změna režimu (pc / tablet / mobile) za běhu – např. ribbon do kompaktní podoby. */
  setMode?(mode: 'pc' | 'tablet' | 'mobile'): void;
}

let zCounter = 10;

export class IwWindow extends HTMLElement {
  readonly wid: string;
  state: WindowState = 'normal';
  geometry: WindowGeometry = { x: 80, y: 60, w: 900, h: 600 };
  private restoreGeometry: WindowGeometry | null = null;
  modal = false;
  /** Okno otevřené jako detail (dříve modálně): Uložit / Zahodit ho zavře, Esc taky, do rozložení se neukládá. */
  closeOnSave = false;
  /** Nejmenší velikost (postup 64): výchozí 480 × 320, okno si může určit jinou (Window::minSize). */
  minW = 480;
  minH = 320;
  parent: IwWindow | null = null;
  app = '';
  windowId = '';
  params: Record<string, unknown> = {};
  view: WindowView | null = null;
  identityLabel: string | null = null;
  iconName: string | null = null;
  private titleEl = h('span', { class: 'win-title' });
  private iconEl = h('span', { class: 'win-icon' });
  private bodyEl = h('div', { class: 'win-body' });
  /** záhlaví s ovládáním (koncepce A): vlevo ☰ / ←, za názvem lišta příkazů, hledání, ⚙ */
  private headStart = h('span', { class: 'win-head-start' });
  private headMain = h('div', { class: 'win-head-main' });
  private titleBar: HTMLElement;
  private btnMax: HTMLButtonElement;
  onClose: (() => void) | null = null;
  onFocus: (() => void) | null = null;
  onChange: (() => void) | null = null;
  onMinimize: (() => void) | null = null;
  /** Obnovení z minimalizace (telefon: znovu krok pro zpět). */
  onRestore: (() => void) | null = null;
  onMenu: (() => MenuItem[]) | null = null;

  constructor(wid = '') {
    super();
    this.wid = wid;
    this.btnMax = h('button', { type: 'button', class: 'win-btn max', title: t('core.ui.maximize') }, icon('maximize')) as HTMLButtonElement;
    this.titleBar = h(
      'div',
      { class: 'win-titlebar' },
      this.headStart,
      this.iconEl,
      this.titleEl,
      h('span', { class: 'win-identity' }),
      this.headMain,
      h('div', { class: 'win-buttons' }, h('button', { type: 'button', class: 'win-btn', title: t('core.ui.minimize'), onClick: () => this.minimize() }, icon('minimize')), this.btnMax, h('button', { type: 'button', class: 'win-btn close', title: t('core.ui.close'), onClick: () => void this.close() }, icon('close'))),
    );
    this.btnMax.addEventListener('click', () => this.toggleMaximize());
  }

  connectedCallback(): void {
    if (this.hasChildNodes()) return;
    this.append(this.titleBar, this.bodyEl, ...['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].map((d) => h('div', { class: 'win-resize ' + d, dataset: { dir: d } })));
    this.setAttribute('role', 'dialog');
    this.tabIndex = -1;
    this.addEventListener('pointerdown', () => this.focusWindow(), true);
    this.installDrag();
    this.installResize();
    this.titleBar.addEventListener('dblclick', (e) => {
      if (IwWindow.interactive(e.target)) return;
      this.toggleMaximize();
    });
    contextMenu(this.titleBar, () => this.onMenu?.() ?? this.defaultMenu());
    this.apply();
  }

  defaultMenu(): MenuItem[] {
    return [
      { label: t('core.ui.restore'), icon: 'restore', disabled: this.state === 'normal', onSelect: () => this.restore() },
      { label: t('core.ui.minimize'), icon: 'minimize', onSelect: () => this.minimize() },
      { label: t('core.ui.maximize'), icon: 'maximize', disabled: this.state === 'maximized', onSelect: () => this.maximize() },
      { separator: true, label: '' },
      {
        label: t('core.cmd.shortcut'),
        icon: 'shortcut',
        disabled: this.modal,
        onSelect: () => {
          const d = (window as unknown as { iwanDesktop?: { createShortcut(app: string, win: string, params: Record<string, unknown>, name: string, icon?: string): Promise<void> } }).iwanDesktop;
          void d?.createShortcut(this.app, this.windowId, this.params, this.titleText(), this.iconName ?? undefined);
        },
      },
      { separator: true, label: '' },
      { label: t('core.ui.close'), icon: 'close', onSelect: () => void this.close() },
    ];
  }

  setTitle(title: string, iconName?: string): void {
    this.titleEl.textContent = title;
    if (iconName) {
      this.iconName = iconName;
      this.iconEl.replaceChildren(icon(iconName));
    }
    this.onChange?.();
  }

  /** Záhlaví s ovládáním: pohled vloží ☰ / ← (start) a lištu, hledání, ⚙ (main); null = obyčejný titulek. */
  setHeader(parts: { start?: Node[]; main?: Node[] } | null): void {
    this.headStart.replaceChildren(...(parts?.start ?? []));
    this.headMain.replaceChildren(...(parts?.main ?? []));
    this.classList.toggle('has-header', parts !== null);
  }

  /** Prvek, na který se kliká (tlačítko, pole) – ten okno nepřesouvá ani nemaximalizuje. */
  private static interactive(target: EventTarget | null): boolean {
    return target instanceof Element && Boolean(target.closest('button, input, select, textarea, a, label, iw-commandbar .cbar-btn, .no-drag'));
  }

  setIdentity(label: string | null): void {
    this.identityLabel = label;
    const el = this.querySelector('.win-identity');
    if (el) el.textContent = label ? `· ${label}` : '';
    this.classList.toggle('run-as', Boolean(label));
  }

  titleText(): string {
    return this.titleEl.textContent ?? '';
  }

  body(): HTMLElement {
    return this.bodyEl;
  }

  /** Před stavbou jiného pohledu v témže okně: záhlaví a stav starého pryč (nový si je postaví sám, např. pás karet místo lišty). */
  resetChrome(): void {
    this.setHeader(null);
    this.classList.remove('sel-mode', 'search-open', 'pc-search', 'dirty');
  }

  setView(view: WindowView): void {
    this.view?.destroy?.();
    this.view = view;
    this.bodyEl.replaceChildren(view.el);
  }

  setModal(parent: IwWindow | null): void {
    this.modal = true;
    this.parent = parent;
    this.classList.add('modal');
    this.querySelector<HTMLElement>('.win-buttons .win-btn')?.remove(); // bez minimalizace
  }

  // ---- stavy

  focusWindow(): void {
    this.style.zIndex = String(++zCounter);
    this.classList.add('focused');
    this.onFocus?.();
  }

  blurWindow(): void {
    this.classList.remove('focused');
  }

  minimize(): void {
    if (this.modal) return;
    this.state = 'minimized';
    this.classList.add('minimized');
    this.onMinimize?.();
    this.onChange?.();
  }

  maximize(): void {
    if (this.state === 'normal') this.restoreGeometry = { ...this.geometry };
    this.state = 'maximized';
    this.classList.remove('minimized');
    this.apply();
    this.onChange?.();
  }

  restore(): void {
    if (this.state === 'maximized' && this.restoreGeometry) this.geometry = { ...this.restoreGeometry };
    this.state = 'normal';
    this.classList.remove('minimized');
    this.apply();
    this.focusWindow();
    this.onChange?.();
  }

  unminimize(): void {
    this.state = this.restoreGeometry && this.classList.contains('maximized') ? 'maximized' : 'normal';
    this.classList.remove('minimized');
    this.onRestore?.();
    this.apply();
    this.focusWindow();
    this.onChange?.();
  }

  toggleMaximize(): void {
    if (this.state === 'maximized') this.restore();
    else this.maximize();
  }

  async close(force = false): Promise<boolean> {
    if (!force && this.view?.canClose && !(await this.view.canClose())) return false;
    this.view?.destroy?.();
    this.classList.add('closing');
    window.setTimeout(() => this.remove(), 120);
    this.onClose?.();
    return true;
  }

  setGeometry(g: Partial<WindowGeometry>): void {
    Object.assign(this.geometry, g);
    this.apply();
  }

  forceMaximized(on: boolean): void {
    this.classList.toggle('force-max', on);
    this.apply();
  }

  apply(): void {
    const area = this.parentElement?.getBoundingClientRect() ?? { width: window.innerWidth, height: window.innerHeight };
    const max = this.state === 'maximized' || this.classList.contains('force-max');
    this.classList.toggle('maximized', max);
    this.btnMax.replaceChildren(icon(max ? 'restore' : 'maximize'));
    this.btnMax.title = max ? t('core.ui.restore') : t('core.ui.maximize');
    if (max) {
      this.style.left = '0';
      this.style.top = '0';
      this.style.width = '100%';
      this.style.height = '100%';
      this.classList.toggle('narrow', area.width < 640);
      this.classList.toggle('compact', area.width < 900);
      return;
    }
    const g = this.geometry;
    const minW = Math.min(this.minW, area.width);
    const minH = Math.min(this.minH, area.height);
    g.w = clamp(g.w, minW, Math.max(minW, area.width));
    g.h = clamp(g.h, minH, Math.max(minH, area.height));
    this.classList.toggle('narrow', g.w < 640); // záhlaví: hledání jako lupa, Zobrazení v ⋯
    this.classList.toggle('compact', g.w < 900); // záhlaví: užší hledání, ať se na lištu vejdou akce
    g.x = clamp(g.x, -g.w + 120, area.width - 120);
    g.y = clamp(g.y, 0, Math.max(0, area.height - 40));
    this.style.left = g.x + 'px';
    this.style.top = g.y + 'px';
    this.style.width = g.w + 'px';
    this.style.height = g.h + 'px';
  }

  // ---- přesun a změna velikosti

  private installDrag(): void {
    this.titleBar.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || IwWindow.interactive(e.target)) return;
      if (this.classList.contains('force-max')) return;
      e.preventDefault();
      const start = { x: e.clientX, y: e.clientY, gx: this.geometry.x, gy: this.geometry.y };
      let dragging = false;
      const area = this.parentElement?.getBoundingClientRect();
      this.titleBar.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        if (!dragging && Math.abs(dx) + Math.abs(dy) < 4) return;
        if (!dragging) {
          dragging = true;
          this.classList.add('dragging');
          if (this.state === 'maximized') {
            // tažením z maximalizace: obnovit a chytit pod kurzorem
            const g = this.restoreGeometry ?? { ...this.geometry, w: Math.min(this.geometry.w, 900), h: Math.min(this.geometry.h, 600) };
            this.state = 'normal';
            this.geometry = { ...g, x: ev.clientX - (area?.left ?? 0) - g.w / 2, y: ev.clientY - (area?.top ?? 0) - 16 };
            start.gx = this.geometry.x;
            start.gy = this.geometry.y;
            start.x = ev.clientX;
            start.y = ev.clientY;
            this.apply();
          }
        }
        this.geometry.x = start.gx + (ev.clientX - start.x);
        this.geometry.y = start.gy + (ev.clientY - start.y);
        this.apply();
        this.snapHint(ev, area);
      };
      const up = (ev: PointerEvent) => {
        this.titleBar.removeEventListener('pointermove', move);
        this.titleBar.removeEventListener('pointerup', up);
        this.classList.remove('dragging');
        document.querySelector('.snap-hint')?.remove();
        if (dragging) {
          this.snap(ev, area);
          this.onChange?.();
        }
      };
      this.titleBar.addEventListener('pointermove', move);
      this.titleBar.addEventListener('pointerup', up);
    });
  }

  private snapZone(ev: PointerEvent, area?: DOMRect): 'left' | 'right' | 'top' | null {
    if (!area || this.modal) return null; // modální okno se nepřichytává ani nemaximalizuje tažením
    const x = ev.clientX - area.left;
    const y = ev.clientY - area.top;
    if (y <= 4) return 'top';
    if (x <= 6) return 'left';
    if (x >= area.width - 6) return 'right';
    return null;
  }

  private snapHint(ev: PointerEvent, area?: DOMRect): void {
    const zone = this.snapZone(ev, area);
    let hint = document.querySelector<HTMLElement>('.snap-hint');
    if (!zone) {
      hint?.remove();
      return;
    }
    if (!hint) {
      hint = h('div', { class: 'snap-hint' });
      this.parentElement?.appendChild(hint);
    }
    hint.dataset['zone'] = zone;
  }

  private snap(ev: PointerEvent, area?: DOMRect): void {
    const zone = this.snapZone(ev, area);
    if (!zone || !area) return;
    if (zone === 'top') {
      this.maximize();
      return;
    }
    this.restoreGeometry = null;
    this.state = 'normal';
    const half = Math.floor(area.width / 2);
    this.geometry = { x: zone === 'left' ? 0 : half, y: 0, w: half, h: area.height };
    this.apply();
  }

  private installResize(): void {
    this.querySelectorAll<HTMLElement>('.win-resize').forEach((handle) => {
      handle.addEventListener('pointerdown', (e) => {
        if (this.state === 'maximized' || this.classList.contains('force-max')) return;
        e.preventDefault();
        e.stopPropagation();
        const dir = handle.dataset['dir'] ?? '';
        const start = { x: e.clientX, y: e.clientY, g: { ...this.geometry } };
        handle.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent) => {
          const dx = ev.clientX - start.x;
          const dy = ev.clientY - start.y;
          const g = { ...start.g };
          if (dir.includes('e')) g.w = start.g.w + dx;
          if (dir.includes('s')) g.h = start.g.h + dy;
          if (dir.includes('w')) {
            g.w = start.g.w - dx;
            g.x = start.g.x + dx;
          }
          if (dir.includes('n')) {
            g.h = start.g.h - dy;
            g.y = start.g.y + dy;
          }
          if (g.w < this.minW) {
            if (dir.includes('w')) g.x = start.g.x + start.g.w - this.minW;
            g.w = this.minW;
          }
          if (g.h < this.minH) {
            if (dir.includes('n')) g.y = start.g.y + start.g.h - this.minH;
            g.h = this.minH;
          }
          this.geometry = g;
          this.apply();
        };
        const up = () => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          this.onChange?.();
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
      });
    });
  }
}

customElements.define('iw-window', IwWindow);
